import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Combobox } from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, Loader2, FileText, Search, Filter, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

const SEV_STYLE = {
  Low: "bg-slate-100 text-slate-600", Medium: "bg-rose-100 text-[#bf3b4f]",
  High: "bg-orange-100 text-orange-700", Critical: "bg-red-100 text-red-700",
};
const STATUS_STYLE = {
  Submitted: "bg-amber-100 text-amber-800", "Under Review": "bg-rose-100 text-[#bf3b4f]",
  Returned: "bg-purple-100 text-purple-700", Closed: "bg-slate-100 text-slate-500",
};
const STATUS_TABS = [["open", "Open"], ["Submitted", "New"], ["Under Review", "Under Review"], ["open_actions", "Open Actions"], ["Closed", "Closed"], ["", "All"]];

const dl = (blob, filename) => {
  const url = URL.createObjectURL(blob); const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export default function Incidents() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(STATUS_TABS.some(([v]) => v === searchParams.get("status")) ? searchParams.get("status") : "open");
  const [severity, setSeverity] = useState("");
  const [q, setQ] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.incidents({ ...(tab === "open_actions" ? { open_actions: 1 } : { status: tab || undefined }), severity: severity || undefined, q: q || undefined })
      .then(setRows).catch(() => toast.error("Could not load incidents")).finally(() => setLoading(false));
  };
  useEffect(load, [tab, severity]); // eslint-disable-line

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <div>
            <h1 className="text-lg font-bold text-slate-900 tracking-tight">Incident Management</h1>
            <p className="text-[11px] text-slate-400">Review, investigate and close reported incidents.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {can.has("incidents.reports") && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setReportOpen(true)} data-testid="stakeholder-report-open">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Stakeholder report
            </Button>
          )}
          {can.has("incidents.create") && (
            <Button size="sm" className="h-8 text-xs bg-[#bf3b4f] hover:bg-[#a32f42]" onClick={() => nav("/incidents/new")} data-testid="incident-new-open">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Log incident
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg mb-3">
        <div className="flex gap-1 px-2 pt-1 border-b border-slate-100">
          {STATUS_TABS.map(([v, lbl]) => (
            <button key={v || "all"} onClick={() => setTab(v)} data-testid={`inc-tab-${v || "all"}`}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === v ? "border-red-600 text-red-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 p-2.5">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="inc-filter-severity"
            className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-700">
            <option value="">All severities</option>
            {["Low", "Medium", "High", "Critical"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-1 flex-1 max-w-xs">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search participant / worker / ID" className="h-8 text-xs pl-7" data-testid="inc-search" />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load}>Go</Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-400" data-testid="inc-empty">No incidents match these filters.</div>
        ) : (
          <table className="w-full text-xs" data-testid="incidents-table">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2">Incident</th><th className="px-2 py-2">When</th><th className="px-2 py-2">Participant</th>
              <th className="px-2 py-2">Worker</th><th className="px-2 py-2">Types</th><th className="px-2 py-2">Severity</th>
              <th className="px-2 py-2">Status</th>{tab === "open_actions" && <th className="px-2 py-2">Open actions</th>}<th className="px-2 py-2">Flags</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} onClick={() => nav(`/incidents/${r.name}`)} data-testid={`incident-row-${r.name}`}
                  className="border-t border-slate-100 hover:bg-red-50/40 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.name}{r.source === "Office" && <span className="ml-1.5 font-sans rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[9px] font-semibold" title="Logged by the office">Office</span>}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-slate-600">{r.incident_datetime ? dayjs(r.incident_datetime).format("D MMM, HH:mm") : "—"}</td>
                  <td className="px-2 py-2 text-slate-700">{r.participant_name || "—"}</td>
                  <td className="px-2 py-2 text-slate-500">{r.support_worker_name || "—"}</td>
                  <td className="px-2 py-2 text-slate-500">{(r.types || []).join(", ") || "—"}</td>
                  <td className="px-2 py-2"><span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${SEV_STYLE[r.severity] || "bg-slate-100"}`}>{r.severity}</span></td>
                  <td className="px-2 py-2"><span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[r.status] || "bg-slate-100"}`}>{r.status}</span></td>
                  {tab === "open_actions" && (
                    <td className="px-2 py-2 whitespace-nowrap" data-testid={`incident-open-actions-${r.name}`}>
                      <span className="rounded bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">{r.open_action_count} open</span>
                      {r.overdue_action_count > 0 && <span className="ml-1 rounded bg-red-600 text-white px-2 py-0.5 text-[10px] font-semibold">{r.overdue_action_count} overdue</span>}
                    </td>
                  )}
                  <td className="px-2 py-2 space-x-1">
                    {r.reportable ? <span className="rounded bg-red-50 text-red-600 border border-red-100 px-1.5 py-0.5 text-[9px] font-semibold">NDIS</span> : null}
                    {r.notifiable_stakeholders ? <span className="rounded bg-emerald-50 text-[#3f9963] border border-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold">Stakeholder</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <StakeholderReportDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}

function StakeholderReportDialog({ open, onClose }) {
  const { can } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [participant, setParticipant] = useState(null);
  const [from, setFrom] = useState(dayjs().startOf("year").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState("");
  const [summarising, setSummarising] = useState(false);

  useEffect(() => { if (open) api.participants().then(setParticipants).catch(() => {}); }, [open]);
  const pOpts = useMemo(() => participants.map((p) => ({ value: p.name, label: p.display_name || p.name })), [participants]);
  const selectedNames = () => Object.entries(sel).filter(([, v]) => v).map(([k]) => k);

  const preview = async () => {
    if (!participant) return toast.error("Choose a participant");
    setLoading(true); setItems(null); setSummary("");
    try {
      const res = await api.incidents({ participant, notifiable: 1, date_from: from, date_to: to });
      setItems(res); setSel(Object.fromEntries(res.map((r) => [r.name, true])));
    } catch { toast.error("Could not load incidents"); } finally { setLoading(false); }
  };

  const genSummary = async () => {
    const names = selectedNames();
    if (names.length === 0) return toast.error("Select at least one incident");
    setSummarising(true);
    try {
      const res = await api.stakeholderSummary({ participant, date_from: from, date_to: to, names: names.join(",") });
      setSummary(res.summary || "");
      toast.success("AI summary drafted — review and edit before exporting");
    } catch (e) { toast.error(e.response?.data?.detail || "AI summary failed"); } finally { setSummarising(false); }
  };

  const exportPdf = async () => {
    const names = selectedNames();
    if (names.length === 0) return toast.error("Select at least one incident");
    setExporting(true);
    try {
      const blob = await api.stakeholderReport({ participant, date_from: from, date_to: to, names: names.join(","), summary });
      dl(blob, `Stakeholder-Incidents-${participant}-${from}_to_${to}.pdf`);
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="stakeholder-report-dialog">
        <DialogHeader><DialogTitle className="text-base">Stakeholder incident report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-slate-500">Export a PDF of all stakeholder-notifiable incidents for a participant over a timeframe.</p>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Participant</label>
            <div className="mt-1"><Combobox testid="sr-participant" options={pOpts} value={participant} onChange={(v) => { setParticipant(v); setItems(null); }} placeholder="Select participant" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-medium text-slate-600">From</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-xs mt-1" data-testid="sr-from" /></div>
            <div><label className="text-[11px] font-medium text-slate-600">To</label><Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-9 text-xs mt-1" data-testid="sr-to" /></div>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs w-full" onClick={preview} disabled={loading} data-testid="sr-load">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Find notifiable incidents"}
          </Button>
          {items && (
            <div className="border border-slate-200 rounded-md max-h-52 overflow-auto" data-testid="sr-list">
              {items.length === 0 ? <div className="p-4 text-center text-xs text-slate-400">No stakeholder-notifiable incidents in this period.</div> : items.map((r) => (
                <label key={r.name} className="flex items-center gap-2 px-3 py-2 border-b border-slate-50 last:border-0 text-xs cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={!!sel[r.name]} onChange={(e) => setSel((s) => ({ ...s, [r.name]: e.target.checked }))} data-testid={`sr-check-${r.name}`} className="accent-red-600" />
                  <span className="font-mono text-[11px] text-slate-500">{r.name}</span>
                  <span className="text-slate-600">{r.incident_datetime ? dayjs(r.incident_datetime).format("D MMM YYYY") : ""}</span>
                  <span className={`ml-auto rounded px-1.5 py-0.5 text-[9px] font-semibold ${SEV_STYLE[r.severity]}`}>{r.severity}</span>
                </label>
              ))}
            </div>
          )}
          {items && items.length > 0 && (
            <div className="space-y-1.5" data-testid="sr-summary-block">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-medium text-slate-600">AI incident summary <span className="text-slate-400">(editable · appears at the top of the report)</span>
                  {can.has("settings.ai") && <Link to="/settings/ai/incidents" className="ml-1.5 text-slate-400 hover:text-slate-600 underline" data-testid="sr-ai-settings">AI settings</Link>}
                </label>
                <Button size="sm" variant="ghost" className="h-7 text-[11px] text-[#3f9963] hover:text-[#2f7d54] hover:bg-emerald-50" onClick={genSummary} disabled={summarising} data-testid="sr-generate-summary">
                  {summarising ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" />{summary ? "Regenerate" : "Generate AI summary"}</>}
                </Button>
              </div>
              <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={6} data-testid="sr-summary-text"
                placeholder="Click “Generate AI summary” to draft a plain-language overview of the selected incidents, then edit it here before exporting. (Optional — leave blank to omit.)"
                className="text-xs" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={exportPdf} disabled={exporting || !items || items.length === 0} data-testid="sr-export">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileText className="h-3.5 w-3.5 mr-1" />Export PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
